import { ArrowLeft, RotateCw } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { Link as AriaLink } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { isRouteErrorResponse, useLocation, useNavigate, useRouteError } from 'react-router';

import { ApiError } from '@/api/client.ts';
import Button from '@/core/components/Button.tsx';
import { GhostCard } from '@/core/components/EmptyPage.tsx';
import { UnexpectedErrorActionContext } from '@/core/components/ErrorMessage.tsx';
import Page from '@/core/components/Page.tsx';
import { recordError } from '@/core/errorLog.ts';
import useFormat from '@/core/i18n/useFormat.ts';
import { ReportProblemButton } from '@/support/ReportProblemDialog.tsx';
import { classnames } from '@/utils/classnames.ts';
import { joinDetails } from '@/utils/joinDetails.ts';

// The page's cards falling out of its lower right corner, cut off by it and faded towards the text; for a page that
// doesn't exist, a grid with one of them missing.
const CornerCards = ({ isNotFound }: { isNotFound: boolean }) => (
    <div
        aria-hidden
        className="pointer-events-none absolute -right-16 -bottom-24 h-80 w-[36rem] [mask-image:linear-gradient(to_top_left,black_40%,transparent_85%)]"
    >
        {isNotFound ? (
            <div className="grid grid-cols-2 gap-4 pt-10 pl-20">
                <GhostCard />
                <span className="flex items-center justify-center rounded-card border-2 border-dashed border-border text-4xl font-semibold text-text-subtle">
                    ?
                </span>
                <GhostCard titleWidth="w-20" />
                <GhostCard titleWidth="w-32" />
            </div>
        ) : (
            <>
                <GhostCard className="absolute top-4 left-10 w-56 -rotate-12" />
                <GhostCard titleWidth="w-20" className="absolute top-20 left-56 w-56 rotate-[20deg]" />
                <GhostCard titleWidth="w-32" className="absolute top-36 left-24 w-56 rotate-3" />
            </>
        )}
    </div>
);

interface ErrorPageProps {
    // Rendered in the app's frame (rail and top bar), where it fills the scrolling area instead of the window.
    isInAppFrame?: boolean;
}

const ErrorPage = ({ isInAppFrame = false }: ErrorPageProps) => {
    const { t } = useTranslation();
    const { formatDateTime } = useFormat();
    const error = useRouteError();
    const location = useLocation();
    const navigate = useNavigate();
    // Only inside the session, where a problem can be reported (plan task 98).
    const canReport = useContext(UnexpectedErrorActionContext) !== null;
    const [occurredAt] = useState(() => new Date().toISOString());
    // Rendered directly as the catch-all route element, where there is no error object.
    const isNotFound =
        !error ||
        (isRouteErrorResponse(error) && error.status === 404) ||
        (error instanceof ApiError && error.status === 404);
    // The tab's first entry has no page of the app before it to go back to.
    const canGoBack = location.key !== 'default';

    useEffect(() => {
        if (!isNotFound) recordError(error);
    }, [error, isNotFound]);

    return (
        <div
            className={classnames(
                'relative flex flex-col overflow-hidden',
                // Down to the screen's edge, where the cards are cut off: the app frame's scrolling area or the window.
                isInAppFrame ? 'min-h-full' : 'min-h-dvh'
            )}
        >
            <CornerCards isNotFound={isNotFound} />
            <div className="relative mx-auto flex w-full max-w-250 flex-1 flex-col items-start justify-center px-4 py-14 pb-[12%] md:px-8">
                <Page.Header className="mb-8">
                    <Page.Heading>
                        <Page.Eyebrow size="lg">
                            {isNotFound ? t('errorPage.notFoundEyebrow') : t('errorPage.errorEyebrow')}
                        </Page.Eyebrow>
                        <Page.Title size="lg" className="max-w-xl">
                            {isNotFound ? t('common.pageNotFound') : t('common.somethingWentWrong')}
                        </Page.Title>
                        <Page.Lead>
                            {isNotFound
                                ? t('errorPage.notFoundText')
                                : canReport
                                  ? t('errorPage.errorText')
                                  : t('errorPage.errorTextSignedOut')}
                        </Page.Lead>
                    </Page.Heading>
                </Page.Header>
                <div className="flex flex-wrap gap-2">
                    {isNotFound ? (
                        <>
                            <Button asChild variant="primary">
                                <AriaLink href="/">{t('common.backToDashboard')}</AriaLink>
                            </Button>
                            {canGoBack && (
                                <Button onClick={() => navigate(-1)}>
                                    <ArrowLeft />
                                    {t('common.back')}
                                </Button>
                            )}
                        </>
                    ) : (
                        <>
                            <Button variant="primary" onClick={() => window.location.reload()}>
                                <RotateCw />
                                {t('errorPage.reload')}
                            </Button>
                            {/* Not the context's button: that's the notices' small one, and here it stands beside a large one. */}
                            {canReport && <ReportProblemButton size="md" />}
                        </>
                    )}
                </div>
                <div className="mt-10 flex w-full max-w-xl flex-col gap-1 border-t border-border pt-4 text-xs text-text-subtle">
                    <span className="font-mono break-all">
                        {window.location.host}
                        {location.pathname}
                        {location.search}
                    </span>
                    <span>
                        {joinDetails([
                            formatDateTime(occurredAt),
                            !isNotFound && canReport && t('errorPage.errorLogSentAlong'),
                        ])}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default ErrorPage;
